import {
  ColorSelectorTypes,
  StockStatuses
} from '@dcxwam/dcx-wam-ui-foundations'
import type { ChangeEvent, MouseEvent } from 'react'
import {
  AttributeSelectorItemProps,
  AttributeSelectorProps
} from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/product/common/attribute-selector/types'
import type { ColorProps } from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/product/common/color-selector/types'
import {
  ProductDataVariant,
  VariantWithDynamicAttributes
} from '../../../services/product/product-data'
import { ProductStockData } from '../../../services/stock'
import { SEO_REPLACE_PLACEHOLDER } from '../../../utils/constants'
import { stockStatusMap } from '../ProductList/utils'
import {
  AttributeSelectorItem,
  AttributeSelectorMarkers,
  GeneralDataConfig
} from './types'
import { formatPrice, FormatPriceOptions } from '../../../utils/format-price'
import { isMinQuantityExceedsStock } from '../../../utils/quantity-selector'

type VariantStockMap = Map<string, ProductStockData | ProductDataVariant>

const getVariantAttributeValue = (
  variant: ProductDataVariant,
  attributeName: string
): string | undefined => {
  const value = (variant as VariantWithDynamicAttributes)[attributeName]
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'number') {
    return value.toString()
  }
  return undefined
}

const matchesAttributes = (
  variant: ProductDataVariant,
  attributeNames: string[] | undefined,
  selectedAttributes: Record<string, string> | undefined
): boolean =>
  attributeNames?.every((attributeName) => {
    const selectedValue = selectedAttributes?.[attributeName]
    if (selectedValue === undefined) {
      return true
    }
    return getVariantAttributeValue(variant, attributeName) === selectedValue
  }) ?? true

const resolveStockStatus = (status?: string): StockStatuses | undefined => {
  if (!status) {
    return undefined
  }
  if (Object.prototype.hasOwnProperty.call(stockStatusMap, status)) {
    return stockStatusMap[status as keyof typeof stockStatusMap]
  }
  return undefined
}

const getVariantStockStatus = (
  stockMap: VariantStockMap | undefined,
  variant?: ProductDataVariant
): string | undefined => {
  if (!variant?.globalArticleCode) {
    return undefined
  }
  return stockMap?.get(variant.globalArticleCode)?.stock?.stockLevelStatus
}

export const splitProductCode = (productCode: string) =>
  productCode?.split('.')[0]

export const findProduct = (props: {
  selectedColorValue?: string
  selectedAttributes?: Record<string, string>
  generalProductConfig?: GeneralDataConfig
  variants?: ProductDataVariant[]
  attributeNames?: string[]
}) => {
  const {
    selectedColorValue,
    selectedAttributes,
    generalProductConfig,
    variants,
    attributeNames
  } = props
  let currentColorVariant: ProductDataVariant | undefined

  if (!generalProductConfig?.groupByFlavour) {
    currentColorVariant = variants?.find(
      (variant: ProductDataVariant) =>
        splitProductCode(variant.pmiInternalProductCode ?? '') ===
          selectedColorValue &&
        matchesAttributes(variant, attributeNames, selectedAttributes)
    )

    if (attributeNames?.length && !currentColorVariant) {
      return variants?.find(
        (variant: ProductDataVariant) =>
          splitProductCode(variant.pmiInternalProductCode ?? '') ===
          selectedColorValue
      )
    }
  } else if (generalProductConfig?.groupByFlavour) {
    currentColorVariant = variants?.find(
      (variant: ProductDataVariant) =>
        variant?.nuance?.code === selectedColorValue &&
        matchesAttributes(variant, attributeNames, selectedAttributes)
    )
  }
  return currentColorVariant
}

export const getColorSelectorProperties = (props: {
  variants?: ProductDataVariant[]
  currentProductStockMap?: VariantStockMap
  productRootPath?: string
  generalConfig?: GeneralDataConfig
  currentAttributeSelectedValues?: Record<string, string>
  outOfStockValue?: string
  attributeNames?: string[]
  currentProductVariant?: ProductDataVariant
  colorName?: string
  onChange?: (
    e: MouseEvent<HTMLElement> | ChangeEvent<HTMLInputElement>,
    clickedColor: ColorProps
  ) => void
  type?: ColorSelectorTypes
  colorGroupAriaLabel?: string
  id?: string
}) => {
  const {
    variants,
    currentProductStockMap,
    productRootPath,
    generalConfig,
    currentAttributeSelectedValues,
    outOfStockValue,
    attributeNames,
    currentProductVariant,
    colorName,
    type,
    colorGroupAriaLabel,
    onChange,
    id
  } = props
  const groupByFlavour = Boolean(generalConfig?.groupByFlavour)

  const currentColorCode = !groupByFlavour
    ? splitProductCode(currentProductVariant?.pmiInternalProductCode ?? '')
    : currentProductVariant?.nuance?.code

  const seenVariantCodes = new Set<string>()

  const buildLinkUrl = (variant: ProductDataVariant): string | undefined => {
    const seoUrl = variant?.seo?.productsSeoUrl ?? ''
    if (!seoUrl) {
      return undefined
    }
    return productRootPath
      ? productRootPath.replace(SEO_REPLACE_PLACEHOLDER, seoUrl)
      : seoUrl
  }

  const buildColorVariant = (
    variant: ProductDataVariant
  ): ColorProps | null => {
    const variantCode = groupByFlavour
      ? variant?.nuance?.code
      : splitProductCode(variant?.pmiInternalProductCode ?? '')

    if (!variantCode || seenVariantCodes.has(variantCode)) {
      return null
    }

    const colorProductVariant = findProduct({
      selectedColorValue: variantCode,
      selectedAttributes: currentAttributeSelectedValues,
      generalProductConfig: generalConfig,
      variants,
      attributeNames
    })

    const stockLevelStatus = getVariantStockStatus(
      currentProductStockMap,
      colorProductVariant
    )

    const variantStockLevel = colorProductVariant?.globalArticleCode
      ? currentProductStockMap?.get(colorProductVariant.globalArticleCode)
          ?.stock?.stockLevel
      : undefined

    const stock = isMinQuantityExceedsStock(
      colorProductVariant?.minimumOrderQuantity,
      variantStockLevel
    )
      ? resolveStockStatus('outOfStock')
      : resolveStockStatus(stockLevelStatus)

    const description = variant?.nuance?.description ?? ''

    const ariaLabel =
      stockLevelStatus && stockLevelStatus === outOfStockValue && description
        ? `${description}-out-of-stock`
        : description

    seenVariantCodes.add(variantCode)

    return {
      value: variantCode,
      linkUrl: buildLinkUrl(variant),
      colorLabel: variant?.nuance?.description,
      colorHex: variant?.nuance?.hexCode,
      ...(stock ? { stock } : {}),
      ...(ariaLabel ? { 'aria-label': ariaLabel } : {})
    }
  }

  const colorVariants =
    variants
      ?.map(buildColorVariant)
      .filter((variant): variant is ColorProps => Boolean(variant)) ?? []

  return {
    ...(id ? { id } : {}),
    currentValue: currentColorCode,
    name: colorName,
    showText: true,
    onChange,
    type,
    'aria-label': colorGroupAriaLabel,
    ...(colorVariants.length ? { colorVariants } : {})
  }
}

export const getAttributesSelectorProps = (props: {
  attributeNames?: string[]
  attributeSelectorMap?: Record<string, string>
  currentAttributeSelectedValues?: Record<string, string>
  attributeNameOptions?: Record<string, string[]>
  markers?: AttributeSelectorMarkers[]
  generalConfig?: GeneralDataConfig
  currentProductVariant?: ProductDataVariant
  variants?: ProductDataVariant[]
  attributeSelector?: AttributeSelectorItem[]
  onChange?: (
    e: ChangeEvent<HTMLInputElement>,
    attributeVariant: AttributeSelectorItemProps
  ) => void
  pageLocale?: string
  currentProductStockMap?: VariantStockMap
  formatPriceOptions?: FormatPriceOptions
}) => {
  const {
    attributeNames,
    attributeSelectorMap,
    currentAttributeSelectedValues,
    attributeNameOptions,
    markers,
    generalConfig,
    currentProductVariant,
    variants,
    attributeSelector,
    onChange,
    pageLocale,
    currentProductStockMap,
    formatPriceOptions
  } = props
  return attributeNames?.map((attributeName) => {
    const attributeSelectorProps: AttributeSelectorProps = {
      label: attributeSelectorMap?.[attributeName],
      name: attributeName,
      currentValue: currentAttributeSelectedValues?.[attributeName],
      attributeVariants: attributeNameOptions?.[attributeName]?.reduce(
        (acc: Array<AttributeSelectorItemProps>, option: string) => {
          const matchingAttSelectorMarkers =
            markers?.length &&
            markers?.find(
              (sm: AttributeSelectorMarkers) => sm?.value === option
            )?.label

          let attributeProductVariant
          let currentColorAndAttrVariant

          if (!generalConfig?.groupByFlavour) {
            const currentPmiInternalProductCode = splitProductCode(
              currentProductVariant?.pmiInternalProductCode ?? ''
            )

            attributeProductVariant = findProduct({
              selectedColorValue: currentPmiInternalProductCode,
              selectedAttributes: {
                ...currentAttributeSelectedValues,
                [attributeName]: option
              },
              generalProductConfig: generalConfig,
              variants,
              attributeNames
            })

            currentColorAndAttrVariant = variants?.filter(
              (variant: ProductDataVariant) =>
                splitProductCode(variant.pmiInternalProductCode ?? '') ===
                  currentPmiInternalProductCode &&
                getVariantAttributeValue(variant, attributeName) === option
            )?.[0]
          } else if (generalConfig?.groupByFlavour) {
            attributeProductVariant = findProduct({
              selectedColorValue: currentProductVariant?.nuance?.code,
              selectedAttributes: {
                ...currentAttributeSelectedValues,
                [attributeName]: option
              },
              generalProductConfig: generalConfig,
              variants,
              attributeNames
            })

            currentColorAndAttrVariant = variants?.filter(
              (variant: ProductDataVariant) =>
                variant?.nuance?.code === currentProductVariant?.nuance?.code &&
                getVariantAttributeValue(variant, attributeName) === option
            )?.[0]
          }

          const shouldShowPrice = attributeSelector?.some(
            (selector: AttributeSelectorItem) =>
              selector.name === attributeName && selector.showPrice
          )

          const currentVariantFormattedPrice = formatPrice({
            value: currentColorAndAttrVariant?.price?.grossPrice,
            currency: currentColorAndAttrVariant?.price?.priceCurrency,
            locale: pageLocale,
            options: formatPriceOptions
          })

          const attributeLabelOption =
            shouldShowPrice && currentVariantFormattedPrice
              ? `${option}<br>${currentVariantFormattedPrice}`
              : option

          const attributeSelectorMarkerLabel =
            shouldShowPrice &&
            currentVariantFormattedPrice &&
            matchingAttSelectorMarkers
              ? `${matchingAttSelectorMarkers}<br>${currentVariantFormattedPrice}`
              : matchingAttSelectorMarkers

          const attributeLabel =
            markers && markers.length > 0
              ? (attributeSelectorMarkerLabel ?? attributeLabelOption)
              : attributeLabelOption

          const stockLevelStatus = getVariantStockStatus(
            currentProductStockMap,
            attributeProductVariant
          )

          const attrVariantStockLevel =
            attributeProductVariant?.globalArticleCode
              ? currentProductStockMap?.get(
                  attributeProductVariant.globalArticleCode
                )?.stock?.stockLevel
              : undefined

          const resolvedStockStatus = isMinQuantityExceedsStock(
            attributeProductVariant?.minimumOrderQuantity,
            attrVariantStockLevel
          )
            ? resolveStockStatus('outOfStock')
            : resolveStockStatus(stockLevelStatus)

          if (currentColorAndAttrVariant) {
            const attributeItem: AttributeSelectorItemProps = {
              value: option
            }

            if (attributeLabel) {
              attributeItem.attributeLabel = attributeLabel
            }

            if (resolvedStockStatus) {
              attributeItem.stock = resolvedStockStatus
            }

            acc.push(attributeItem)
          }
          return acc
        },
        []
      ),
      onAttributeChange: onChange
    }
    return attributeSelectorProps
  })
}

export const handleAttributeSelectorChange = (props: {
  attributeOption: AttributeSelectorItemProps
  config: GeneralDataConfig
  productVariants: ProductDataVariant[]
  attributeNames: string[]
  currentAttributeSelectedValues?: Record<string, string>
  attributeName: string
  currentProductVariant: ProductDataVariant
}) => {
  const {
    attributeOption,
    config,
    productVariants,
    attributeNames,
    currentAttributeSelectedValues,
    attributeName,
    currentProductVariant
  } = props

  const newProductAttributeSelectedValues: Record<string, string> = {
    ...(currentAttributeSelectedValues ?? {}),
    ...(attributeOption?.value !== undefined
      ? { [attributeName]: attributeOption.value }
      : {})
  }

  let clickedVariant: ProductDataVariant | undefined

  if (!config?.groupByFlavour) {
    clickedVariant = findProduct({
      selectedColorValue: splitProductCode(
        currentProductVariant?.pmiInternalProductCode ?? ''
      ),
      selectedAttributes: newProductAttributeSelectedValues,
      generalProductConfig: config,
      variants: productVariants,
      attributeNames
    })
  } else if (config?.groupByFlavour) {
    clickedVariant = findProduct({
      selectedColorValue: currentProductVariant?.nuance?.code,
      selectedAttributes: newProductAttributeSelectedValues,
      generalProductConfig: config,
      variants: productVariants,
      attributeNames
    })
  }

  return {
    clickedVariant,
    newProductAttributeSelectedValues
  }
}
